import { commonStrings } from "@src/constants/common-strings";
import { BreadcrumbHandleData } from "@components/header/sphere-breadcrumbs";
import { ProjectArchivingState } from "@custom-types/project-types";
import { Breadcrumb } from "@flat-ui";
import { MainRoutes, ProjectTabs, RouteParams, UsersTabs } from "@router/route-params";
import { hasUserValidFeatureCompanyLevel } from "@utils/feature-control/company/company-feature-control";
import { RequiredFeatureCompanyLevelName } from "@utils/feature-control/company/company-feature-control-types";
import { getPrettyName } from "@utils/user-utils";

/** Breadcrumbs can provide a callback to change their content dynamically */
export type BreadcrumbCallback = (
  params: Partial<RouteParams>,
  data: BreadcrumbHandleData
) => Breadcrumb | Breadcrumb[] | null;

/** Single object for a breadcrumb handle element. */
export interface BreadcrumbHandle {
  /**
   * Could be a direct breadcrumb or a function that returns a breadcrumb,
   * for example to include data from loaders or to include the selected project.
   */
  breadcrumb?: Breadcrumb | BreadcrumbCallback;
}

/** Returns the breadcrumb handle for the route "/projects" */
export function getProjectsBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      return {
        text: commonStrings.PROJECTS,
        link: `/${params.companyId}/${MainRoutes.projects}/${ProjectArchivingState.active}${data.queryParams || ""}`,
      };
    },
  };
}

/** Returns the breadcrumb handle for the route "/projects/:projectStatus/:projectId" */
export function getProjectBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      const { companyId, projectId } = params;
      return {
        text: data?.selectedProject?.name ?? projectId ?? "",
        link: `/${companyId}/${MainRoutes.projects}/${projectId}/${ProjectTabs.overview}${data.queryParams || ""}`,
        shouldNotTranslate: true,
      };
    },
  };
}

/** Returns the breadcrumb handle for the route "/members" */
export function getMembersBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      const canViewAllCompanyUsers = hasUserValidFeatureCompanyLevel({
        featureName: RequiredFeatureCompanyLevelName.canViewCompanyMembers,
        featuresAvailable: data.companyContext?.featuresAvailable,
      });
      return {
        text: commonStrings.MEMBERS,
        link: `/${params.companyId}/${MainRoutes.members}${data.queryParams || ""}`,
        isDisabled: !canViewAllCompanyUsers,
      };
    },
  };
}

/**
 * Returns the breadcrumb handle for the route "/members/:memberId"
 */
export function getMemberProfileBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      let text;
      // If the member is the current user, use that object to react to changes for name, etc.
      if (params.memberId && params.memberId === data.currentUser?.identity) {
        text = getPrettyName(data.currentUser);
      } else {
        text = getPrettyName(data.selectedMember);
      }
      if (!text && params.memberId) {
        text = params.memberId;
      }

      return {
        text,
        shouldNotTranslate: true,
      };
    },
  };
}

/** Returns the breadcrumb handle for the route "/groups" */
export function getGroupsBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      return {
        text: commonStrings.GROUPS,
        link: `/${params.companyId}/${MainRoutes.groups}${data.queryParams || ""}`,
      };
    },
  };
}

/**
 * Returns the breadcrumb handle for the route "/groups/:groupId"
 */
export function getGroupBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      return {
        text: data?.selectedGroup?.name ?? params.groupId ?? "",
        shouldNotTranslate: true,
      };
    },
  };
}

/** Returns the breadcrumb handle for the route "/analytics" */
export function getAnalyticsBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      return {
        text: commonStrings.ANALYTICS,
        link: `/${params.companyId}/${MainRoutes.analytics}${data.queryParams || ""}`,
      };
    },
  };
}

/** Returns the breadcrumb handle for the route "/settings" */
export function getWorkspaceSettingsBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      return {
        text: commonStrings.SETTINGS,
        link: `/${params.companyId}/${MainRoutes.settings}${data.queryParams || ""}`,
      };
    },
  };
}

/** Returns the breadcrumb handle for the route "/user-groups/:teamId" */
export function getTeamBreadcrumbHandle(): BreadcrumbHandle {
  return {
    breadcrumb: (params: Partial<RouteParams>, data: BreadcrumbHandleData) => {
      return [
        {
          text: `${commonStrings.TEAMS}`,
          link: `/${params.companyId}/${MainRoutes.members}/${UsersTabs.teams}${data.queryParams || ""}`,
        },
        {
          text: data?.selectedTeam?.name ?? params.teamId ?? "",
          link: `/${params.companyId}/${MainRoutes.teams}${data.queryParams || ""}`,
          shouldNotTranslate: true,
        },
      ];
    },
  };
}
